/* eslint-disable @tencent/i18next/no-literal-string */
import _set from 'lodash/set';
import { BaseChartPresetHandler } from '@src/slide/domain/ui-model/element-modeles/chart-models/chart-presets/sub-chart-presets/base';
import {
    DEFAULT_SHAPE_PROPS,
    PresetChartType,
} from '@src/slide/domain/ui-model/element-modeles/chart-models/chart-presets/const';
import type {
    ModifyPlotAreaProps,
    SeriesInitialProps,
    SeriesPresetPoint,
} from '@src/slide/domain/ui-model/element-modeles/chart-models/chart-presets/sub-chart-presets/base';
import { IBaseSubChart } from '../../../Interface/chart-v3/sub-chart/base-sub-chart.interface';
import { ChartSeries } from '@src/slide/domain/ui-model/element-modeles/chart-models/sub-chart/series-v2';
import { IRadarChart } from '../../../Interface/chart-v3/sub-chart';
import { RadarChart } from '@src/slide/domain/ui-model/element-modeles/chart-models/sub-chart/radar-chart';
import { IChartAxis, IChartLegend, IChartPlotArea, IChartSpace } from '../../../Interface/chart-v3/chart-properties.interface';
import {
    STCrossBetween,
    STLblAlgn,
    STLegendPos,
    STMarkerStyleDmlC,
    STRadarStyle,
    SubChartType,
} from '../../../Interface/chart-v3/sdk-model.interface';
import { canvasToEmu } from '@src/slide/infrastructure/module/independent/unit';
import { RadarChartSeries } from '@src/slide/domain/ui-model/element-modeles/chart-models/sub-chart/series-v2/radar-chart-series';

// office的时间戳，对应 2002/1/5, 2002/1/6, 2002/1/7, 2002/1/8, 2002/1/9
export const RADAR_CHART_SERIES_CATEGORIES_PRESET = ['37261', '37262', '37263', '37264', '37265'];
export const RADAR_CHART_VALUES_PRESET = [
    [32, 32, 28, 12, 15],
    [12, 12, 12, 21, 28],
];

export class RadarChartPresetHandler extends BaseChartPresetHandler {
    get ChartSeries() {
        return RadarChartSeries;
    }
    get SubChart() {
        return RadarChart;
    }

    protected defaultChartDataPreset = {
        categories: RADAR_CHART_SERIES_CATEGORIES_PRESET,
        values: RADAR_CHART_VALUES_PRESET,
    };

    public createChartModel(
        type: PresetChartType,
        chartSpace: IChartSpace,
        initialProps?: SeriesInitialProps,
    ): IRadarChart {
        const subChart = super.createChartModel(type, chartSpace, initialProps) as IRadarChart;

        switch (type) {
            case PresetChartType.RADAR:
                this.setRadarStandard(subChart);
                break;
            case PresetChartType.RADAR_WITH_FILL:
                this.setRadarFilled(subChart);
                break;
            case PresetChartType.RADAR_WITH_POINT:
                this.setRadarWithPoint(subChart);
                break;
        }

        return subChart;
    }

    public getLegendProps(): Partial<IChartLegend> {
        return {
            position: STLegendPos.t,
        };
    }

    public modifyPlotArea(param: {
        plotArea: IChartPlotArea;
        newSubChart: IBaseSubChart;
        targetType: PresetChartType;
        getChartPresetHandlerByChartBaseType: (type: SubChartType) => BaseChartPresetHandler | undefined;
    }): ModifyPlotAreaProps {
        const baseProps = super.modifyPlotArea(param);

        const { plotArea, newSubChart } = param;

        this.removeAxisTitleIfExist(plotArea, newSubChart, baseProps.removeProps);

        return {
            ...baseProps,
            updateProps: {
                ...baseProps.updateProps,
                valueAxes: [
                    {
                        ...baseProps.updateProps?.valueAxes?.[0],
                        crossBetween: STCrossBetween.between,
                    },
                ],
            },
        };
    }

    public removeAxisTitleIfExist(
        plotArea: IChartPlotArea,
        newSubChart: IBaseSubChart,
        removeProps: ModifyPlotAreaProps['removeProps'],
    ) {
        const relativeAxisIds = newSubChart.axisIds;
        const { valueAxes, dateAxes, categoryAxes } = plotArea;
        [
            { axes: valueAxes, key: 'valueAxes' },
            { axes: categoryAxes, key: 'categoryAxes' },
            { axes: dateAxes, key: 'dateAxes' },
        ].forEach(({ axes, key }) => {
            axes?.forEach((axis, index) => {
                // 只检查最后保留的坐标轴，如果存在坐标轴标题，就删掉
                if (relativeAxisIds?.includes(axis.axId!)) {
                    _set(removeProps!, `${key}[${index}].title`, null);
                }
            });
        });
    }

    public modifySubChartType(
        IBaseSubCharts: IBaseSubChart[],
        type: PresetChartType,
        getChartPresetHandlerByChartBaseType,
        chartSpace: IChartSpace,
    ) {
        const subChart = super.modifySubChartType(
            IBaseSubCharts,
            type,
            getChartPresetHandlerByChartBaseType,
            chartSpace,
        ) as IRadarChart;

        switch (type) {
            case PresetChartType.RADAR:
                this.setRadarStandard(subChart);
                this.clearSeriesFillProps(subChart);
                break;
            case PresetChartType.RADAR_WITH_POINT:
                this.setRadarWithPoint(subChart);
                this.clearSeriesFillProps(subChart);
                break;
            case PresetChartType.RADAR_WITH_FILL:
                this.setRadarFilled(subChart);
                break;
        }

        return subChart;
    }

    protected generatePresetSeriesInitialProps(
        seriesIndex: number,
        points: Array<SeriesPresetPoint>,
        seriesName?: string,
        showDataLabels = false,
    ) {
        const baseProps = super.generatePresetSeriesInitialProps(seriesIndex, points, seriesName, showDataLabels);
        return {
            ...baseProps,
            categoryAxisData: {
                formatCode: 'm/d/yy',
                type: 'nr',
            },
            props: {
                ...baseProps.props,
                outline: {
                    cap: 'rnd',
                    lineJoin: 'round',
                    width: canvasToEmu(4.5),
                },
            },
        };
    }

    protected createCategoryAxis(props: Partial<IChartAxis>, type: PresetChartType): IChartAxis {
        return {
            ...super.createCategoryAxis(props, type),
            lblAlgn: STLblAlgn.ctr,
        };
    }

    protected extendSeries(params: {
        newSer: ChartSeries;
        oldSer: ChartSeries;
        newSubChart: IBaseSubChart;
        oldSubChart: IBaseSubChart;
        newSubChartType: PresetChartType;
        serIndex: number;
    }) {
        super.extendSeries(params);

        const { newSer, oldSer, newSubChart } = params;

        const isMarkerStyle = (newSubChart as IRadarChart).radarStyle === STRadarStyle.marker;
        if (isMarkerStyle) {
            // 按office的逻辑，如果之前图形设置了无边框，就在修改图表的时候移除NoFill属性，否则折线会不显示
            if (oldSer.props?.style?.border?.noFill) {
                delete newSer.props.style.border.noFill;
            }

            newSer.dataList.forEach((point, pointIndex) => {
                const uiDataPoint = point.dataPoint;

                if (uiDataPoint?.props?.style?.border?.noFill) {
                    delete uiDataPoint.props.style.border.noFill;
                }

                // newSubChart没有sdk数据，所以这里用params.oldSubChart来判断是否是主题色
                this.removeDataPointUIDataSchemeClr(uiDataPoint, params.oldSubChart, params.serIndex, pointIndex);
            });
        }
    }

    // g2 雷达图坐标轴传入数值 会显示不正确
    protected needCategoryAxis() {
        return true;
    }

    private clearSeriesFillProps(subChart) {
        subChart.series.map((ser) => {
            delete ser.props.style.fillBackground;
        });
    }

    private setRadarStandard(subChart: IRadarChart) {
        subChart.radarStyle = STRadarStyle.marker;
        this.setLineMarker(subChart, false);
    }

    private setRadarFilled(subChart: IRadarChart) {
        subChart.radarStyle = STRadarStyle.filled;
    }

    private setRadarWithPoint(subChart: IRadarChart) {
        subChart.radarStyle = STRadarStyle.marker;
        this.setLineMarker(subChart, true);
    }

    private setLineMarker(subChart: IRadarChart, hasMarker: boolean) {
        if (!hasMarker) {
            subChart.series?.forEach((ser) => {
                ser.marker = {
                    symbol: STMarkerStyleDmlC.none,
                    props: DEFAULT_SHAPE_PROPS,
                };
            });
        } else {
            subChart.series?.forEach((ser) => {
                ser.marker = {
                    symbol: STMarkerStyleDmlC.circle,
                    size: 5,
                    // 这块自行继承主题色
                    props: {
                        style: {
                            border: {
                                width: 1.5,
                            },
                        },
                    },
                };
            });
        }
    }
}
